import React, { useContext, useEffect, useState, useRef } from 'react'
import { path } from 'ramda'
import { object, node } from 'prop-types'
import localeContext from './Locale'
import useLog from '../hooks/useLog'

const translationContext = React.createContext()
const { Provider } = translationContext

export const TranslationProvider = ({ translations, children }) => {
  const locale = useContext(localeContext)
  const { logContent } = useLog()
  const [translationsByLang, setTranslationsByLang] = useState(null)
  // Use a ref to store already logged missing translations to prevent duplicate logs
  const loggedMissingTranslations = useRef(new Set())

  useEffect(() => {
    const initTranslations = async () => {
      try {
        const transLoader = translations[locale.lang]
        if (typeof transLoader === 'function') {
          setTranslationsByLang(await transLoader())
        } else {
          setTranslationsByLang(transLoader)
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading translations', error)
      }
    }
    initTranslations()
  }, [])

  if (!translationsByLang) {
    return null
  }

  const logMissingTranslation = (translationKey, hasEnFallback) => {
    const logKey = `${translationKey}:${locale.lang}:${hasEnFallback}`

    if (!loggedMissingTranslations.current.has(logKey)) {
      // Only log if we haven't logged this missing translation before
      loggedMissingTranslations.current.add(logKey)

      logContent({
        level: 'warn',
        data: {
          message: 'MISSING_TRANSLATION',
          meta: {
            translationDetails: {
              translationKey,
              locale: locale.lang,
              hasEnFallback
            }
          }
        }
      })
    }
  }

  const translate = (p, options = {}) => {
    const transPath = p.split('.')
    const result = path(transPath, translationsByLang)
    // expectMissing is used to signal that translations are intentionally missing
    const { expectMissing = false } = options

    // If translation is missing, try to fall back to English (for French) or return an empty string
    if (result === undefined && !expectMissing) {
      const enResult = path(transPath, translations.en)
      const hasEnFallback = enResult !== undefined

      logMissingTranslation(p, hasEnFallback)

      return enResult
    }

    return result
  }

  return <Provider value={translate}>{children}</Provider>
}

TranslationProvider.propTypes = {
  translations: object.isRequired,
  children: node.isRequired
}

export default translationContext
